import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MarketLayout from '../../../components/MarketLayout';
import { Icon } from '../../../utils/icons';
// 🔑 THE ONE STAFF COMPOSER (fix/lead-comms-composer, 2026-11-03): the Doorly → investor reply box is StaffComposer (no
// file — the route sends none); the server reads it through prepareStaffMessage, the one allowlist; stored HTML renders
// through RichMessageBody only.
import StaffComposer from '../../../components/admin/StaffComposer';
import RichMessageBody, { hasRichBody } from '../../../components/RichMessageBody';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmt = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export default function MarketMessages() {
  const token = localStorage.getItem('doorly_admin_token');
  const [messages, setMessages] = useState([]);
  const [unreadByInvestor, setUnreadByInvestor] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedInvestorId, setSelectedInvestorId] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/market/admin/messages`, { headers });
      setMessages(res.data?.messages || []);
      setUnreadByInvestor(res.data?.unreadByInvestor || {});
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group messages by investor — most recent first
  const investorRows = (() => {
    const seen = new Map();
    for (const m of messages) {
      if (!seen.has(m.investor_id)) {
        seen.set(m.investor_id, {
          investor_id: m.investor_id,
          name: `${m.investor_first_name || ''} ${m.investor_last_name || ''}`.trim() || m.investor_email,
          firm: m.investor_firm_name || '',
          email: m.investor_email || '',
          last: m,
          unread: unreadByInvestor[m.investor_id] || 0,
        });
      }
    }
    return Array.from(seen.values());
  })();

  const fetchThread = useCallback(async (investorId) => {
    setThreadLoading(true);
    try {
      const res = await axios.get(`${API}/api/market/admin/investors/${investorId}/messages`, { headers });
      setThread(res.data?.messages || []);
    } catch {
      setThread([]);
    } finally {
      setThreadLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedInvestorId) { setThread([]); return; }
    fetchThread(selectedInvestorId);
  }, [selectedInvestorId, fetchThread]);

  const markRead = async (id) => {
    await axios.patch(`${API}/api/market/admin/messages/${id}/read`, {}, { headers }).catch(() => {});
  };

  const handleSelect = async (row) => {
    setSelectedInvestorId(row.investor_id);
    setReplySubject(row.last?.subject ? `Re: ${row.last.subject.replace(/^Re:\s*/i, '')}` : '');
    setError('');
    if (row.unread > 0) {
      // Mark all unread in-thread as read
      const ids = messages.filter((m) => m.investor_id === row.investor_id && m.sender === 'investor' && !m.is_read).map((m) => m.id);
      await Promise.all(ids.map(markRead));
      setMessages((prev) => prev.map((m) => ids.includes(m.id) ? { ...m, is_read: true } : m));
      setUnreadByInvestor((prev) => ({ ...prev, [row.investor_id]: 0 }));
    }
  };

  // The composer's payload ({ body_html, body }) plus the subject. Resolves true when sent; a refusal throws so the
  // composer shows it and keeps the draft.
  const sendReply = async (payload) => {
    if (!selectedInvestorId) return false;
    const subject = replySubject.trim();
    if (!subject) throw new Error('Subject is required.');
    setSending(true);
    setError('');
    try {
      await axios.post(
        `${API}/api/market/admin/investors/${selectedInvestorId}/messages`,
        { ...payload, subject },
        { headers }
      );
      setReplySubject('');
      await fetchAll();
      await fetchThread(selectedInvestorId);
      return true;
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const selectedRow = investorRows.find((r) => r.investor_id === selectedInvestorId);
  const totalUnread = Object.values(unreadByInvestor).reduce((s, n) => s + (n || 0), 0);

  return (
    <MarketLayout>
      <div style={{ padding: '24px 28px', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111111' }}>Investor Messages</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {totalUnread > 0 ? `${totalUnread} unread across ${investorRows.length} investor${investorRows.length === 1 ? '' : 's'}` : `${investorRows.length} investor thread${investorRows.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        {loading ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 60, textAlign: 'center', color: '#9ca3af' }}>
            Loading messages…
          </div>
        ) : investorRows.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 60, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 12px', borderRadius: 12, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chat" size={26} color="#3a6ad9" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111111', marginBottom: 4 }}>No investor messages yet.</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Messages from DoorlyMarket investors will appear here.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* Investor list */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              {investorRows.map((row) => {
                const isSelected = row.investor_id === selectedInvestorId;
                const isUnread = row.unread > 0;
                return (
                  <div key={row.investor_id} onClick={() => handleSelect(row)}
                    style={{
                      padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      background: isSelected ? '#eff3fe' : isUnread ? '#fafcff' : '#fff',
                      borderLeft: isSelected ? '3px solid #3a6ad9' : '3px solid transparent',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isUnread ? '#fafcff' : '#fff'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      {isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3a6ad9' }} />}
                      <div style={{ fontSize: 13, fontWeight: isUnread ? 700 : 600, color: '#111111', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.name}
                      </div>
                      {isUnread && (
                        <span style={{ background: '#ff082f', color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{row.unread}</span>
                      )}
                    </div>
                    {row.firm && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>{row.firm}</div>}
                    <div style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isUnread ? 600 : 400 }}>
                      {row.last?.sender === 'investor' ? '' : '↳ '}{row.last?.subject}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{fmt(row.last?.created_at)}</div>
                  </div>
                );
              })}
            </div>

            {/* Thread + reply */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, minHeight: 480 }}>
              {!selectedRow ? (
                <div style={{ padding: 80, textAlign: 'center', color: '#9ca3af' }}>
                  Select an investor to view the conversation.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #f3f4f6' }}>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#111111' }}>{selectedRow.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {selectedRow.firm ? `${selectedRow.firm} · ` : ''}{selectedRow.email}
                      </div>
                    </div>
                  </div>

                  {threadLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading thread…</div>
                  ) : thread.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No messages yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24, maxHeight: 'calc(100vh - 460px)', overflowY: 'auto' }}>
                      {thread.map((m) => {
                        const fromInvestor = m.sender === 'investor';
                        return (
                          <div key={m.id} style={{
                            background: fromInvestor ? '#f9fafb' : '#eff3fe',
                            border: `1px solid ${fromInvestor ? '#e5e7eb' : '#bfdbfe'}`,
                            borderRadius: 10,
                            padding: '12px 14px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: fromInvestor ? '#374151' : '#3a6ad9' }}>
                                {fromInvestor ? `${m.sender_name || 'Investor'}` : `${m.sender_name || 'Doorly Team'} (Doorly)`}
                              </div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmt(m.created_at)}</div>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#111111', marginBottom: 4 }}>{m.subject}</div>
                            {hasRichBody(m)
                              ? <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}><RichMessageBody msg={m} /></div>
                              : <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Reply composer */}
                  <div style={{ paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                      Reply to {selectedRow.name}
                    </div>
                    <input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} placeholder="Subject" data-testid="market-reply-subject"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, color: '#111111', boxSizing: 'border-box', outline: 'none', marginBottom: 10 }} />
                    <StaffComposer key={selectedInvestorId} token={token} onSend={sendReply} allowAttachment={false}
                      disabled={sending || !replySubject.trim()} testId="market-reply-composer" rows={5}
                      placeholder="Type your reply…" sendLabel={sending ? 'Sending…' : 'Send Reply'} />
                    {error && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 8, color: '#ff082f', fontSize: 13 }}>
                        {error}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </MarketLayout>
  );
}
